import { z } from 'zod';
import { parentConditionValueSchema, } from '../custom-fields/parent-condition-value.js';
export var ResourceType;
(function (ResourceType) {
    ResourceType["LEGISLATIVE_TYPE"] = "legislative_type";
    ResourceType["LEGISLATIVE_ITEM"] = "legislative_item";
    ResourceType["SEAT_APPLICATION_TEMPLATE"] = "seat_application_template";
    ResourceType["SEAT_APPLICATION"] = "seat_application";
    ResourceType["MEETING_BODY_SEAT_TERM_APPLICATION"] = "meeting_body_seat_term_application";
})(ResourceType || (ResourceType = {}));
export const RESOURCE_TYPE_ENUM = z.enum(ResourceType);
const PARENT_CONDITION_PAIRING_REFINE_MESSAGE = 'Both the parent and the value need to be specified for conditional fields';
/**
 * Returns whether a condition value is valid for a parent field.
 *
 * Accepted shapes:
 * - `{ value: boolean }` — CHECKBOX parent
 * - `{ value: "string" }` — SELECT parent (single option)
 * - `{ value: ["a","b"] }` — MULTISELECT parent (array of option strings)
 */
const isValidConditionValue = (val) => {
    const { value } = val;
    if (typeof value === 'boolean') {
        return true;
    }
    if (typeof value === 'string' && value.trim().length > 0) {
        return true;
    }
    if (Array.isArray(value) &&
        value.length > 0 &&
        value.every((v) => typeof v === 'string' && v.trim().length > 0)) {
        return true;
    }
    return false;
};
/**
 * Validates that `parentConditionAssignmentId` and `parentConditionAssignmentValue` are either
 * both absent, both null, or both set with a valid condition value.
 */
const isValidParentConditionAssignmentPair = (data) => {
    const { parentConditionAssignmentId, parentConditionAssignmentValue } = data;
    const parentSent = parentConditionAssignmentId !== undefined;
    const valueSent = parentConditionAssignmentValue !== undefined;
    const bothOmitted = !parentSent && !valueSent;
    const bothNull = parentConditionAssignmentId === null && parentConditionAssignmentValue === null;
    const bothSet = parentSent &&
        valueSent &&
        parentConditionAssignmentId !== null &&
        parentConditionAssignmentValue !== null;
    if (bothOmitted || bothNull) {
        return true;
    }
    if (bothSet) {
        return isValidConditionValue(parentConditionAssignmentValue);
    }
    return false;
};
// Custom Field Assignment schemas
export const customFieldAssignmentCreateSchema = z
    .object({
    customFieldId: z.number().min(1, 'Custom field ID is required'),
    order: z.number().min(0, 'Order must be non-negative').optional(),
    required: z.boolean().default(false),
    nameOverride: z.string().optional(),
    descriptionOverride: z.string().optional(),
    parentConditionAssignmentId: z.number().int().positive().nullable().optional(),
    parentConditionAssignmentValue: parentConditionValueSchema.nullable().optional(),
})
    .refine(isValidParentConditionAssignmentPair, {
    message: PARENT_CONDITION_PAIRING_REFINE_MESSAGE,
});
export const customFieldAssignmentUpdateSchema = z
    .object({
    order: z.number().min(0, 'Order must be non-negative').optional(),
    required: z.boolean().optional(),
    nameOverride: z.string().optional(),
    descriptionOverride: z.string().optional(),
    parentConditionAssignmentId: z.number().int().positive().nullable().optional(),
    parentConditionAssignmentValue: parentConditionValueSchema.nullable().optional(),
})
    .refine(isValidParentConditionAssignmentPair, {
    message: PARENT_CONDITION_PAIRING_REFINE_MESSAGE,
});
export const customFieldAssignmentBulkCreateSchema = z.object({
    customFields: z
        .array(customFieldAssignmentCreateSchema)
        .min(1, 'At least one custom field is required'),
});
export const customFieldAssignmentBulkUpdateSchema = z.object({
    resourceType: RESOURCE_TYPE_ENUM,
    resourceId: z.number(),
    customFields: z.array(z.object({
        id: z.number(),
        order: z.number(),
    })),
});
